import React, { useState } from 'react';
import axios from 'axios';
import { 
  Search, Clock, CheckCircle, XCircle, Briefcase, Building2, 
  ArrowLeft, Eye, X, MapPin, Calendar, DollarSign 
} from 'lucide-react';
import './CandidateAuth.css'; // Re-using existing orange styles

// Helper to display Date
const formatDMY = (isoDate) => {
  if (!isoDate) return 'N/A';
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return 'N/A'; 
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

export default function ApplicationTracker({ onBack }) {
  const [inputs, setInputs] = useState({ email: '', phone: '' });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  // ✅ NEW: State for Modal
  const [selectedApp, setSelectedApp] = useState(null);

  const handleTrack = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSearched(false);
    setResults([]);

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/auth/candidate/track-status`, inputs);
      setResults(res.data);
      setSearched(true);
    } catch (err) {
      setError(err.response?.data?.message || "Tracking failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Status Color Helper
  const getStatusBadge = (status) => {
    let color = '#d97706'; // Pending (Orange)
    let icon = <Clock size={14} />;
    let bg = '#fff7ed';

    if (status && status.includes('Select')) {
      color = '#16a34a'; // Green
      icon = <CheckCircle size={14} />;
      bg = '#f0fdf4';
    } else if (status && status.includes('Reject')) {
      color = '#dc2626'; // Red
      icon = <XCircle size={14} />;
      bg = '#fef2f2';
    }

    return (
      <span style={{ 
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        padding: '6px 12px', borderRadius: '20px',
        color: color, backgroundColor: bg, 
        fontWeight: '600', fontSize: '0.85rem', border: `1px solid ${color}30`
      }}>
        {icon} {status || 'Pending'}
      </span>
    );
  };

  return (
    <div className="candidate-auth-container">
      <div className="candidate-auth-card candidate-wide-card">
        
        {/* Header */}
        <div className="candidate-auth-header">
          <button onClick={onBack} style={{position: 'absolute', top: '20px', left: '20px', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', gap: '5px'}}>
            <ArrowLeft size={18} /> Home
          </button>
          <div className="candidate-icon-badge">
            <Search size={32} color="#ea580c" />
          </div>
          <h1>Track Application Status</h1>
          <p className="candidate-auth-subtitle">
            Enter your details to check the status of applications submitted by you, vendors, or recruiters.
          </p>
        </div>

        <div className="candidate-auth-body">
          
          {/* Search Form */}
          <form onSubmit={handleTrack} className="candidate-form-grid-2" style={{ alignItems: 'end', marginBottom: '30px' }}>
            <div className="candidate-form-group">
              <label>Email Address</label>
              <input 
                type="email" 
                className="tracker-input"
                style={{width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1'}}
                value={inputs.email}
                onChange={(e) => setInputs({...inputs, email: e.target.value})}
                placeholder="e.g. candidate@mail.com"
                required
              />
            </div>
            <div className="candidate-form-group">
              <label>Phone Number</label>
              <input 
                type="text" 
                className="tracker-input"
                style={{width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1'}}
                value={inputs.phone}
                onChange={(e) => setInputs({...inputs, phone: e.target.value})}
                placeholder="e.g. 9876543210"
                required
              />
            </div>
            <button type="submit" className="btn-primary-candidate" style={{height: '46px', margin: 0}} disabled={loading}>
              {loading ? 'Checking...' : 'Track Status'}
            </button>
          </form>

          {/* Error Message */}
          {error && <div className="candidate-error-banner">{error}</div>}

          {/* Results List */}
          {searched && results.length > 0 && (
            <div className="results-list">
              <h3 style={{fontSize: '1.1rem', marginBottom: '15px', color: '#334155'}}>Found {results.length} Applications:</h3>
              <div style={{display: 'flex', flexDirection: 'column', gap: '15px'}}>
                {results.map((app) => (
                  <div key={app.id} style={{
                    border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px',
                    display: 'flex', flexDirection: 'column', gap: '10px',
                    backgroundColor: '#f8fafc', position: 'relative'
                  }}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start'}}>
                      <div>
                        <h4 style={{margin: '0 0 5px 0', fontSize: '1.1rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px'}}>
                           {app.job?.role}
                        </h4>
                        <div style={{display: 'flex', alignItems: 'center', gap: '6px', color: '#64748b', fontSize: '0.9rem'}}>
                          <Building2 size={14}/> {app.job?.companyName}
                        </div>
                      </div>
                      <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                         {getStatusBadge(app.candidateStatus)}
                      </div>
                    </div>
                    
                    <div className="at-result-footer" style={{borderTop: '1px solid #e2e8f0', marginTop: '10px', paddingTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                       <div style={{display: 'flex', gap: '20px', fontSize: '0.85rem', color: '#64748b'}}>
                          <span>Applied: <strong>{new Date(app.submittedAt).toLocaleDateString()}</strong></span>
                          {app.matchScore > 0 && <span>AI Match: <strong>{app.matchScore}%</strong></span>}
                       </div>
                       
                       {/* ✅ VIEW DETAILS BUTTON */}
                       <button 
                         onClick={() => setSelectedApp(app)}
                         style={{
                             background: 'white', border: '1px solid #ea580c', color: '#ea580c',
                             padding: '6px 12px', borderRadius: '6px', cursor: 'pointer',
                             fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px'
                         }}
                       >
                         <Eye size={16} /> View Job
                       </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {searched && results.length === 0 && !error && (
             <div style={{textAlign: 'center', color: '#64748b', padding: '20px'}}>
                No applications found matching these details.
             </div>
          )}

        </div>
      </div>

      {/* ✅ JOB DETAILS MODAL (Same as Candidate Profile) */}
      {selectedApp && (
        <div style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', 
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999
        }}>
            <div className="at-modal-content" style={{
                backgroundColor: 'white', padding: '30px', borderRadius: '12px', width: '600px', maxWidth: '90%', 
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)', position: 'relative', maxHeight: '85vh', overflowY: 'auto'
            }}>
                <button 
                    onClick={() => setSelectedApp(null)}
                    style={{
                        position: 'absolute', top: '20px', right: '20px', background: 'none', border: 'none', cursor: 'pointer'
                    }}
                >
                    <X size={24} color="#64748b" />
                </button>

                <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '15px', marginBottom: '20px' }}>
                    <h2 style={{ fontSize: '1.5rem', color: '#1e293b', marginBottom: '5px' }}>{selectedApp.job?.role}</h2>
                    <div style={{ fontSize: '1rem', color: '#ea580c', fontWeight: '600' }}>{selectedApp.job?.companyName}</div>
                </div>

                <div className="at-modal-info-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                    <div style={{display:'flex', gap:'8px', color:'#475569', fontSize:'0.9rem'}}><MapPin size={16}/> {selectedApp.job?.location || 'N/A'}</div>
                    <div style={{display:'flex', gap:'8px', color:'#475569', fontSize:'0.9rem'}}><Briefcase size={16}/> {selectedApp.job?.typeOfWork || 'Full Time'}</div>
                    <div style={{display:'flex', gap:'8px', color:'#475569', fontSize:'0.9rem'}}><DollarSign size={16}/> {selectedApp.job?.ctc || 'Not Disclosed'}</div>
                    <div style={{display:'flex', gap:'8px', color:'#475569', fontSize:'0.9rem'}}><Calendar size={16}/> Apply By: {formatDMY(selectedApp.job?.applyByDate)}</div>
                </div>

                {selectedApp.job?.aboutJob && (
                    <div style={{ marginTop: '20px' }}>
                        <h4 style={{ color: '#334155', marginBottom: '8px' }}>Job Description</h4>
                        <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: '1.6', whiteSpace: 'pre-line' }}>
                            {selectedApp.job.aboutJob}
                        </p>
                    </div>
                )}

                {selectedApp.job?.aboutCompany && (
                    <div style={{ marginTop: '20px' }}>
                        <h4 style={{ color: '#334155', marginBottom: '8px' }}>About Company</h4>
                        <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: '1.6', whiteSpace: 'pre-line' }}>
                            {selectedApp.job.aboutCompany}
                        </p>
                    </div>
                )}
            </div>
        </div>
      )}

    </div>
  );
}